import type { PageContext } from 'types/page'
import { ExplorePageProps } from 'types/page'
import { combineMetaTags, getSeoMetaObject, MetaTagsInterface } from './MetaTags'
import Logger from 'services/Logger'
import { ContentfulConstraint, ContentfulErrors, RequestRedirect } from 'services/ContentfulService/utils'
import { fetchArticles } from 'services/ContentfulService/hooks/articles'
import { getPageDetails } from 'services/ContentfulService/hooks/pages'
import { Page } from 'services/ContentfulService/models/page'
import { GetServerSideProps, GetServerSidePropsContext, GetServerSidePropsResult } from 'next'
import { getEnv, isProduction } from '../envWrapper'
import { Resource } from 'services/ContentfulService/models/resource/resource'
import { asyncNoOp } from '../index'
import { removeUndefs } from '../objects/removeUndefineds'
import { FeaturedConstraint } from 'services/ContentfulService/constraints'
import { summarizeArticles } from 'services/ContentfulService/models/articles'
import { ObjectNotFoundError } from 'services/ContentfulService/errors'
import { Assert } from 'lib/assertions/assert'
import { SmallerThan } from 'lib/assertions/sizeAssertion'
import { ParsedUrlQuery } from 'querystring'

interface PageDetails {
	page?: Page
	metaTags: Partial<MetaTagsInterface>
	resources?: Resource[]
}

const attemptGetPageFromCMS = async (resolvedPath: string, pathOverride?: string): Promise<PageDetails> => {
	try {
		const page = await getPageDetails(pathOverride || resolvedPath)
		const metaTags = {
			imageUrl: page?.ogImage?.url,
			url: page?.canonical || `${getEnv().root_domain}${resolvedPath}`,
			title: page?.title,
			description: page?.description
		}
		return {
			page,
			metaTags,
			resources: page.resources
		}
	} catch (err) {
		return {
			page: undefined,
			metaTags: {
				url: `${getEnv().root_domain}${resolvedPath}`
			},
			resources: []
		}
	}
}

export const explorePageInitialProps =
	(fixedPathName: string, articleQuery?: ContentfulConstraint) =>
	async (ctx: PageContext): Promise<PagePropsWrapper<ExplorePageProps>> => {
		const { pathname } = ctx
		const articles = articleQuery
			? fetchArticles({ constraints: [articleQuery, FeaturedConstraint()], count: 3 }).then(articles =>
					summarizeArticles(articles)
			  )
			: Promise.resolve([])
		const pageDetails = await attemptGetPageFromCMS(pathname, fixedPathName)
		//pageDetails.resources && store.dispatch(addContentfulResources(pageDetails.resources))
		return {
			metaTags: combineMetaTags([pageDetails.metaTags]),
			pageProps: { articles: await articles },
			parameters: ctx.query,
			resources: pageDetails?.resources || []
		}
	}

export interface PagePropsWrapper<T> {
	metaTags: MetaTagsInterface
	parameters?: ParsedUrlQuery
	resources?: Resource[]
	pageProps: T
}

type CustomSSRFn<T> = (ctx: GetServerSidePropsContext) => Promise<T>
type CustomSSRPropsFunction<T> = (ctx: GetServerSidePropsContext, page?: Page) => Promise<T>
type CustomInitialPropsFunction<T> = (ctx: PageContext, page?: Page) => Promise<T>
type MetadataTagsOverrideFunction<T> = (metadataTags: MetaTagsInterface, pageProps: T) => MetaTagsInterface

export const SEOFriendlyInitialProps =
	<T>(
		getPageProps: CustomInitialPropsFunction<T>,
		overrideMetatags?: MetadataTagsOverrideFunction<T>,
		pageResourceOverride?: string
	) =>
	async (ctx: PageContext): Promise<PagePropsWrapper<T>> => {
		try {
			const path = ctx.asPath || ctx.pathname
			let page: Page | undefined = undefined
			try {
				page = await getPageDetails(pageResourceOverride || path)
			} catch (err) {}
			const standardMetatags = getSeoMetaObject(path, page)
			const pageProps = await getPageProps(ctx, page)
			const metaTags = overrideMetatags ? overrideMetatags(standardMetatags, pageProps) : standardMetatags
			return { metaTags, pageProps, resources: page?.resources || [] }
		} catch (err) {
			Logger.error(err)
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			return { notFound: true }
		}
	}

export const getSEOServersideProps =
	<T>(
		getPageProps: CustomSSRPropsFunction<T>,
		overrideMetatags?: MetadataTagsOverrideFunction<T>,
		pageResourceOverride?: string
	) =>
	async (ctx: GetServerSidePropsContext): Promise<GetServerSidePropsResult<PagePropsWrapper<T>>> => {
		try {
			const pathname = ctx.resolvedUrl
			const pageDetails = await attemptGetPageFromCMS(pathname, pageResourceOverride)
			const pageProps = await getPageProps(ctx, pageDetails.page)
			const standardMetatags = getSeoMetaObject(pathname, pageDetails.page)
			const metaTags = overrideMetatags ? overrideMetatags(standardMetatags, pageProps) : standardMetatags
			isProduction && Assert.log(pageProps, [SmallerThan(50000)])
			return { props: JSON.parse(JSON.stringify({ metaTags, pageProps, resources: pageDetails.resources })) }
		} catch (err) {
			if (err === ContentfulErrors.NotFound) return { notFound: true }
			if (err instanceof RequestRedirect) {
				if (err.redirect) return { redirect: { permanent: false, destination: err.path } }
			}
			Logger.error(err)
			throw err
		}
	}

export const getServerSidePropsWrapper =
	<T>(fn: CustomSSRFn<T>) =>
	async (context: GetServerSidePropsContext): Promise<GetServerSidePropsResult<T>> => {
		try {
			const page = await fn(context)
			return { props: removeUndefs(page) }
		} catch (err) {
			if (err === ContentfulErrors.NotFound || err instanceof ObjectNotFoundError) return { notFound: true }
			if (err instanceof RequestRedirect) {
				if (err.redirect) return { redirect: { permanent: false, destination: err.path } }
			}
			throw err
		}
	}

export const defaultSSRProps: GetServerSideProps = getSEOServersideProps<void>(asyncNoOp, tags => tags)
